import { h } from 'preact'
import { useState } from 'preact/hooks'
import classNames from 'classnames'
import { i18n } from 'i18n/i18n'

import { Calculation } from 'models/exchange-transfer-calculate'

import { formatAmountWithCurrency, formatNumberTrimZeros, formatNumberWithSep, formatRate } from 'utils/format-number'

import SummaryRow from './summary-row'

interface CalculatorSummaryProps {
  exchangeCalculation: Calculation
  equivalentFee?: string
  countdown: number
}

const CalculatorSummary = ({ exchangeCalculation, equivalentFee, countdown }: CalculatorSummaryProps) => {
  const [isSummaryOpen, setIsSummaryOpen] = useState(false)

  const { sold_currency: soldCurrency, bought_currency: boughtCurrency } = exchangeCalculation
  const rate = formatRate(exchangeCalculation.average_rate, `${boughtCurrency}_${soldCurrency}`)
  const commissionTooltipText = `${i18n('transferCalculator.commissionAmount', {
    fee_percent: exchangeCalculation.fee_percent ? formatNumberTrimZeros(exchangeCalculation.fee_percent) : '',
  })} ${soldCurrency}`
  const transferFeeTooltipText = `${i18n('transferCalculator.equivalentFee')} ${formatAmountWithCurrency(
    equivalentFee,
  )}`

  return (
    <div className="transfer-calculator__summary">
      <div className="transfer-calculator__summary-header">
        <div className="transfer-calculator__summary-header-info">
          <p className="transfer-calculator__summary-text">
            {i18n('transferCalculator.exchangeRates')}: {rate}
          </p>
          <p className="transfer-calculator__summary-text">
            {i18n('timer.update')} {countdown} {i18n('timer.timeUnit')}
          </p>
        </div>
        <button
          className={classNames('transfer-calculator__summary-toggler', {
            'transfer-calculator__summary-toggler--active': isSummaryOpen,
          })}
          data-show-more
          type="button"
          aria-expanded={isSummaryOpen}
          onClick={() => setIsSummaryOpen((prevIsSummaryOpen) => !prevIsSummaryOpen)}
        >
          {i18n('transferCalculator.details')}
        </button>
      </div>

      {isSummaryOpen && (
        <div className="transfer-calculator__summary-body">
          <SummaryRow
            isBold={true}
            label={i18n('transferCalculator.amountYouSend')}
            value={`${formatNumberWithSep(exchangeCalculation.bought_amount)} ${boughtCurrency}`}
          />

          <div className="transfer-calculator__summary-details">
            <SummaryRow
              label={i18n('transferCalculator.exchangeRates')}
              value={`x ${rate}`}
            />
            <SummaryRow
              label={i18n('transferCalculator.exchangeAmount')}
              value={`= ${formatNumberWithSep(exchangeCalculation.exchange_amount)} ${soldCurrency}`}
            />
            <SummaryRow
              label={i18n('transferCalculator.commissionWT')}
              value={`- ${formatNumberWithSep(exchangeCalculation.fee_amount)} ${soldCurrency}`}
              tooltipText={commissionTooltipText}
            />
            <SummaryRow
              label={i18n('transferCalculator.foreignTransferFee')}
              value={`-${formatAmountWithCurrency(exchangeCalculation.transfer_type_fee)}`}
              tooltipText={transferFeeTooltipText}
            />
          </div>

          <SummaryRow
            isBold={true}
            label={i18n('transferCalculator.amountRecipientReceive')}
            value={`${formatNumberWithSep(exchangeCalculation.sold_amount)} ${soldCurrency}`}
          />
        </div>
      )}
    </div>
  )
}

export default CalculatorSummary
